import { useCallback, useMemo, useState } from 'react'

import { AppTemplate } from 'features/templates/types'

import useDebounce from 'v2/ui/utils/useDebounce'

const SEARCH_QUERY_DEBOUNCE_MS = 500

type UseAppTemplateListStateProps = {
    templates?: AppTemplate[]
    isLoading?: boolean
    isError?: boolean
}

export const useAppTemplateListState = ({
    templates,
    isLoading,
    isError,
}: UseAppTemplateListStateProps) => {
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const setDebouncedQuery = useDebounce(setDebouncedSearchQuery, SEARCH_QUERY_DEBOUNCE_MS)

    const onSearchQueryChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value

            setDebouncedQuery(value)
        },
        [setDebouncedQuery]
    )

    const effectiveTemplates = isLoading ? generatePlaceholderTemplates() : templates

    const filteredTemplates = useMemo(() => {
        if (!effectiveTemplates) return []

        return filterTemplates(effectiveTemplates, debouncedSearchQuery)
    }, [effectiveTemplates, debouncedSearchQuery])

    const isEmpty = effectiveTemplates?.length === 0 && !isLoading && !isError
    const isEmptySearch =
        debouncedSearchQuery.length > 0 && filteredTemplates.length < 1 && !isError

    return {
        templates: filteredTemplates,
        onSearchQueryChange,
        isEmpty,
        isEmptySearch,
    }
}

const PLACEHOLDER_TEMPLATE_COUNT = 6
const PLACEHOLDER_TEMPLATE: AppTemplate = {
    id: '1',
    name: 'Loading...',
    description: 'Loading...',
    contents: [],
    version: 1,
    bundle_data: {},
    tags: ['Loading...', 'Loading...'],
    color_scheme: 'Pink',
    icon: {
        name: 'House',
    },
}

function generatePlaceholderTemplates() {
    return Array.from({ length: PLACEHOLDER_TEMPLATE_COUNT }, (_, index) => ({
        ...PLACEHOLDER_TEMPLATE,
        id: `placeholder-${index}`,
    }))
}

const searchFields: (keyof AppTemplate)[] = ['name', 'description', 'tags']

export function filterTemplates(templates: AppTemplate[], searchQuery: string) {
    const normalizedSearchQuery = searchQuery.toLowerCase().trim()

    return templates.filter((t) => {
        return searchFields.some((field) => {
            const value = t[field]

            if (typeof value === 'string') {
                return value.toLowerCase().includes(normalizedSearchQuery)
            } else if (Array.isArray(value)) {
                return value.some((v) => {
                    if (typeof v === 'string') {
                        return v.toLowerCase().includes(normalizedSearchQuery)
                    }

                    return false
                })
            }

            return false
        })
    })
}
